import React, { useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Info, ArrowRight } from "lucide-react";
import { useGet } from "@/lib/hooks";
import { ParcelCard } from "@/components/wp/ParcelCard";
import { ProductCard } from "@/components/wp/ProductCard";
import { SectionHeading } from "@/components/wp/SectionHeading";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RECIPIENTS, OCCASIONS, BUDGETS, filterParcels, discoveryTitle, CURATED_NOTE } from "@/lib/discovery";

const Chip = ({ active, children, ...props }) => (
  <button {...props}
    className={`flex-shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${active ? "border-transparent bg-wp-rose-strong text-white" : "border-border bg-card text-wp-ink hover:bg-wp-blush-50"}`}>
    {children}
  </button>
);

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const recipient = params.get("recipient") || "";
  const occasion = params.get("occasion") || "";
  const budget = params.get("budget") || "all";
  const collection = params.get("collection") || "";
  const [view, setView] = React.useState("parcels");
  const [userPicked, setUserPicked] = React.useState(false);

  const { data: collections } = useGet("/collections");
  const { data: parcels, loading: pLoading } = useGet("/parcels");
  const { data: products, loading: prLoading } = useGet("/products");

  // If there are no visible gift parcels (e.g. all hidden), default to the tab that has content.
  React.useEffect(() => {
    if (userPicked || pLoading || prLoading) return;
    if ((parcels || []).length === 0 && (products || []).length > 0) setView("products");
  }, [userPicked, pLoading, prLoading, parcels, products]);

  const scope = useMemo(() => {
    let base = parcels || [];
    if (collection) base = base.filter((p) => (p.collections || []).includes(collection));
    return base;
  }, [parcels, collection]);

  const { list, curated } = useMemo(
    () => filterParcels(scope, { recipient, occasion, budget }),
    [scope, recipient, occasion, budget]
  );

  const activeCollection = (collections || []).find((c) => c.slug === collection);
  const title = recipient || occasion
    ? discoveryTitle({ recipient, occasion })
    : activeCollection ? `${activeCollection.emoji || ""} ${activeCollection.name}`.trim() : "Explore Gifts";
  const subtitle = activeCollection?.tagline || "Beautiful, gift-ready parcels — curated and ready to send.";

  const merge = (patch) => {
    const next = { recipient, occasion, budget, collection, ...patch };
    const clean = {};
    Object.entries(next).forEach(([k, v]) => { if (v && v !== "all") clean[k] = v; });
    setParams(clean);
  };
  const pickRecipient = (key) => setParams(key === recipient ? {} : { recipient: key, ...(budget !== "all" ? { budget } : {}) });
  const pickOccasion = (key) => setParams(key === occasion ? {} : { occasion: key, ...(budget !== "all" ? { budget } : {}) });

  const filtersActive = recipient || occasion || collection || budget !== "all";

  return (
    <div className="wp-container py-10 sm:py-14">
      <SectionHeading eyebrow="Explore" title={title} subtitle={subtitle} />

      {/* ---- Filters ---- */}
      <div className="mt-6 space-y-3" data-testid="explore-filters">
        <div>
          <p className="mb-2 wp-label text-[10px] text-wp-ink-muted">For</p>
          <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {RECIPIENTS.map((r) => (
              <Chip key={r.key} active={recipient === r.key} onClick={() => pickRecipient(r.key)} data-testid={`filter-recipient-${r.key}`}>{r.label}</Chip>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-2 wp-label text-[10px] text-wp-ink-muted">Occasion</p>
          <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {OCCASIONS.map((o) => (
              <Chip key={o.key} active={occasion === o.key} onClick={() => pickOccasion(o.key)} data-testid={`filter-occasion-${o.key}`}>{o.label}</Chip>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="wp-label mr-1 text-[10px] text-wp-ink-muted">Budget</p>
          {BUDGETS.map((b) => (
            <Chip key={b.key} active={budget === b.key} onClick={() => merge({ budget: b.key })} data-testid={`filter-budget-${b.key}`}>{b.label}</Chip>
          ))}
          {filtersActive && (
            <button onClick={() => setParams({})} data-testid="filter-clear" className="ml-1 text-sm font-medium text-wp-rose-strong hover:underline">Clear all</button>
          )}
        </div>
      </div>

      {/* ---- Curated fallback note ---- */}
      {curated && (
        <div className="mt-5 flex items-start gap-2.5 rounded-wp-lg border border-wp-blush-200 bg-wp-blush-50 px-4 py-3 text-sm text-wp-ink" data-testid="explore-curated-note">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-wp-rose-strong" />
          <span>{CURATED_NOTE}</span>
        </div>
      )}

      {/* ---- View toggle ---- */}
      <div className="mt-6">
        <Tabs value={view} onValueChange={(v) => { setUserPicked(true); setView(v); }}>
          <TabsList data-testid="shop-view-tabs">
            <TabsTrigger value="parcels" data-testid="shop-tab-parcels">Gift Parcels</TabsTrigger>
            <TabsTrigger value="products" data-testid="shop-tab-products">Individual Accessories</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {view === "parcels" ? (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4" data-testid="shop-parcels-grid">
          {pLoading
            ? Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[4/5] w-full rounded-wp-lg" />)
            : list.length > 0
              ? list.map((p, i) => <ParcelCard key={p.id} parcel={p} index={i} />)
              : (
                <div className="col-span-2 rounded-wp-lg border border-border bg-wp-surface p-10 text-center lg:col-span-4" data-testid="shop-parcels-empty">
                  <p className="font-display text-lg">No gift parcels available right now</p>
                  <p className="mt-1 text-sm text-wp-ink-muted">Explore our individual accessories, or build your own parcel.</p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    <Button onClick={() => { setUserPicked(true); setView("products"); }} className="rounded-xl bg-wp-rose-strong text-white hover:brightness-110">Shop accessories</Button>
                    <Button onClick={() => navigate("/build")} variant="outline" className="rounded-xl">Build your parcel</Button>
                  </div>
                </div>
              )}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4" data-testid="shop-products-grid">
          {prLoading
            ? Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-square w-full rounded-wp-lg" />)
            : (products || []).length > 0
              ? (products || []).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)
              : (
                <div className="col-span-2 rounded-wp-lg border border-border bg-wp-surface p-10 text-center lg:col-span-4" data-testid="shop-products-empty">
                  <p className="font-display text-lg">No items to show yet</p>
                  <p className="mt-1 text-sm text-wp-ink-muted">Please check back soon.</p>
                </div>
              )}
        </div>
      )}

      {/* Founder link (kept accessible) */}
      <div className="mt-12 flex flex-col items-center gap-2 rounded-wp-xl border border-border bg-wp-surface p-8 text-center">
        <p className="font-display text-xl">Looking for something truly one-of-a-kind?</p>
        <p className="text-sm text-wp-ink-muted">Discover our limited, hand-made Founder Collection.</p>
        <Button onClick={() => navigate("/founder")} variant="outline" className="mt-2 rounded-xl" data-testid="shop-founder-link">View the collection <ArrowRight className="ml-1 h-4 w-4" /></Button>
      </div>
    </div>
  );
}
